import { UseInfiniteQueryResult, UseQueryResult } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import dayjs from 'dayjs'
import Link from 'next/link'
import { Fragment, useMemo } from 'react'
import { BiWallet } from 'react-icons/bi'
import { FaHandshake } from 'react-icons/fa'
import { MdOutlineMarkEmailRead, MdReply } from 'react-icons/md'
import { InView } from 'react-intersection-observer'
import Alert from 'components/Alert'
import AlgoPrice from 'components/AlgoPrice'
import Button from 'components/Button'
import ButtonLink from 'components/ButtonLink'
import Loading from 'components/Loading'
import UsdPrice from 'components/UsdPrice'
import UserThumbnail from 'components/UserThumbnail'
// import Filters from '../Filters'
import useInfiniteOffers from './InfiniteOffers.hooks'
import OfferNote from './OfferNote'
import OffersToggle from './OffersToggle'
import { classNames, truncateAddress } from 'helpers/utilities'
import { NFDAnalyticRecord, NfdRecordCollection } from 'api/api-client'
import { InfiniteAnalyticsQueryResult } from 'api/hooks/useInfiniteAnalytics'
import { ICustomError } from 'api/customError'

interface InfiniteOffersProps {
  sentQuery: UseInfiniteQueryResult<InfiniteAnalyticsQueryResult, ICustomError>
  receivedQuery: UseInfiniteQueryResult<InfiniteAnalyticsQueryResult, ICustomError>
  usersQuery: UseQueryResult<NfdRecordCollection, AxiosError>
  fetchedPages?: Set<number>
  refetchPage?: (page: number) => void
}

export default function InfiniteOffers({
  sentQuery,
  receivedQuery,
  usersQuery,
  fetchedPages,
  refetchPage
}: InfiniteOffersProps) {
  const { offersType, handleOffersTypeChange, handleClickMarkAsRead, handleClickAcceptOffer } =
    useInfiniteOffers()

  const { data, error, isFetchingNextPage, isInitialLoading, fetchNextPage, hasNextPage } =
    offersType === 'sent' ? sentQuery : receivedQuery

  const allOffers = useMemo(() => {
    return data?.pages?.flatMap((page) => page.data.results).filter((offer) => offer !== null) || []
  }, [data])

  const renderAddress = (address: string | undefined, link = true) => {
    if (!address) {
      return <span className="mt-1 text-sm text-gray-700 dark:text-gray-500">N/A</span>
    }

    const nfd = usersQuery.data?.find((user) => user.caAlgo?.some((addr) => addr === address))

    if (nfd) {
      return (
        <UserThumbnail nfd={nfd} className="text-gray-900 dark:text-gray-300" link={link} padded />
      )
    }

    const addr = truncateAddress(address, { array: true })

    return (
      <Link
        href={`/address/${address}`}
        className="inline-flex items-center text-sm text-gray-900 hover:text-brand-500 font-mono dark:text-gray-400 dark:hover:text-brand-500"
      >
        <BiWallet className="flex-shrink-0 mr-1.5 h-5 w-5 my-1.5" aria-hidden="true" />
        {addr[0]}&hellip;{addr[1]}
      </Link>
    )
  }

  const renderRowCells = (result: NFDAnalyticRecord) => {
    return (
      <>
        <td className="w-full py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6 md:w-[25vw] md:max-w-[25vw] xl:w-auto xl:max-w-none dark:text-gray-300">
          <Link
            href={`/name/${result.data?.name}`}
            className="text-gray-900 hover:text-brand-500 outline-brand-500 text-base font-semibold sm:font-medium dark:text-gray-300 dark:hover:text-brand-500"
          >
            {result.data?.name}
          </Link>
          <dl className="font-normal md:hidden">
            <dt className="sr-only sm:hidden">Price</dt>
            <dd className="flex items-center mt-1 text-base font-medium relative top-0.5 sm:hidden dark:text-gray-300">
              <AlgoPrice price={result.price} />
              <UsdPrice price={result.price as number} className="ml-1.5" />
            </dd>
            <dt className="sr-only sm:hidden">From</dt>
            <dd className="flex items-center flex-wrap mt-2 truncate text-gray-500 sm:hidden">
              <span className="mr-4">{renderAddress(result.data?.buyer)}</span>
              <span className="my-3 text-gray-500">{dayjs(result.timestamp).fromNow(true)}</span>
            </dd>
          </dl>
        </td>
        <td className="hidden whitespace-nowrap px-3 py-4 text-base font-medium text-gray-900 sm:table-cell dark:text-gray-300">
          <span className="flex items-center flex-wrap">
            <AlgoPrice price={result.price} className="mr-1.5" />
            <UsdPrice price={result.price as number} />
          </span>
        </td>
        <td className="hidden whitespace-nowrap px-3 py-4 sm:table-cell">
          {renderAddress(offersType === 'received' ? result.data?.buyer : result.data?.seller)}
        </td>
        <td className="hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell">
          {dayjs(result.timestamp).fromNow(true)}
        </td>
        <td
          className={classNames(
            offersType === 'sent' ? 'pl-3 pr-4 sm:pr-6' : 'px-3',
            'relative whitespace-nowrap text-center'
          )}
        >
          {result.data?.note ? (
            <OfferNote note={result.data.note} sender={renderAddress(result.data.buyer, false)} />
          ) : (
            <span className="hidden sm:inline text-gray-400 dark:text-gray-500">--</span>
          )}
        </td>
        <td
          className={classNames(
            offersType === 'sent' ? 'hidden' : '',
            'relative whitespace-nowrap pl-3 pr-4 py-4 sm:pr-6'
          )}
        >
          <span className="flex flex-col-reverse items-center justify-center h-full sm:flex-row sm:space-x-2">
            <ButtonLink
              href={`/sell/${result.data?.name}?reservedFor=${encodeURIComponent(
                result.data?.buyer as string
              )}&sellAmount=${result.price as number}`}
              className="mt-2 sm:mt-0 group"
            >
              <MdReply
                className="hidden xl:inline -ml-1 mr-2 h-5 w-5 text-gray-400 group-hover:text-gray-500 dark:text-gray-500 dark:group-hover:text-gray-400"
                aria-hidden="true"
              />
              Counter
            </ButtonLink>
            <Button
              variant="primary"
              onClick={() => handleClickAcceptOffer(result)}
              className="w-full sm:w-min"
            >
              <FaHandshake className="hidden xl:inline mr-2 h-5 w-5" aria-hidden="true" />
              Accept
            </Button>
          </span>
        </td>
      </>
    )
  }

  const renderRows = (results: NFDAnalyticRecord[] | null, pageIdx: number) => {
    if (!data || !results) {
      return null
    }

    return results.map((result: NFDAnalyticRecord, i) => {
      const handleInViewChange = (inView: boolean) => {
        if (!inView) return

        const isLastPage = pageIdx === data.pages.length - 1
        const needsRefresh = refetchPage && !fetchedPages?.has(pageIdx)

        if (isLastPage && hasNextPage) {
          fetchNextPage()
        } else if (needsRefresh) {
          refetchPage(pageIdx)
        }
      }

      const key = `${result.data?.name}-${result.price}-${result.timestamp}`
      const isLastResult = i === results.length - 1

      if (isLastResult) {
        return (
          <InView key={key} as="tr" onChange={handleInViewChange}>
            {renderRowCells(result)}
          </InView>
        )
      }

      return <tr key={key}>{renderRowCells(result)}</tr>
    })
  }

  const renderTable = () => {
    if (isInitialLoading || usersQuery?.isInitialLoading) {
      return (
        <div className="flex justify-center py-8">
          <Loading />
        </div>
      )
    }

    if (error) {
      return <Alert type="error" title="Error" message="An error occurred while fetching offers." />
    }

    if (allOffers.length === 0) {
      const message = offersType === 'sent' ? 'No sent offers' : 'No received offers'

      return <Alert type="info" title={message} />
    }

    return (
      <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-8">
        <div className="inline-block min-w-full py-2 align-middle md:px-8">
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
            <table className="min-w-full divide-y divide-gray-300 dark:divide-none">
              <thead className="bg-gray-100/50 dark:bg-gray-850/50">
                <tr>
                  <th
                    scope="col"
                    className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 dark:text-gray-500"
                  >
                    <span className="sm:hidden">Offers</span>
                    <span className="hidden sm:inline">Name</span>
                  </th>
                  <th
                    scope="col"
                    className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell dark:text-gray-500"
                  >
                    Price
                  </th>
                  <th
                    scope="col"
                    className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell dark:text-gray-500"
                  >
                    {offersType === 'sent' ? 'To' : 'From'}
                  </th>
                  <th
                    scope="col"
                    className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell capitalize dark:text-gray-500"
                  >
                    {offersType}
                  </th>
                  <th
                    scope="col"
                    className={classNames(
                      offersType === 'sent' ? 'pl-3 pr-4 sm:pr-6' : 'px-3',
                      'relative py-3.5 text-center text-sm font-semibold text-gray-900 dark:text-gray-500'
                    )}
                  >
                    <span className="sr-only sm:hidden">Note</span>
                    <span className="hidden sm:inline">Note</span>
                  </th>
                  <th
                    scope="col"
                    className={classNames(
                      offersType === 'sent' ? 'hidden' : '',
                      'relative py-3.5 pl-3 pr-4 sm:pr-6 text-center text-sm font-semibold text-gray-900 dark:text-gray-500'
                    )}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white dark:bg-gray-850 dark:divide-gray-700/40">
                {data?.pages.map((page, pageIdx) => (
                  <Fragment key={pageIdx}>{renderRows(page.data.results, pageIdx)}</Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div>
      {/* search not yet supported */}
      {/* <Filters
        key={viewId}
        search={search}
        placeholder={searchPlaceholder}
        className="-mt-6 mb-6"
      /> */}

      <div className="flex items-center justify-center">
        <div className="hidden sm:block flex-auto">
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-300">
            <span className="capitalize">{offersType}</span> offers
          </h1>
          <p className="mt-2 text-sm text-gray-700 dark:text-gray-500">
            {offersType === 'received'
              ? `Offers made for NFDs you own, sorted by price.`
              : `Offers you've made for other NFDs.`}{' '}
            Click &quot;Mark all as read&quot; to dismiss.
          </p>
        </div>
        <div className="flex-1 sm:ml-16 sm:flex-none">
          <OffersToggle value={offersType} onChange={handleOffersTypeChange} />
        </div>
      </div>
      <div className="mt-6 flex flex-col">{renderTable()}</div>

      {/* @todo: show discreet table loader */}
      {isFetchingNextPage && (
        <div className="flex flex-col items-center justify-center py-32">
          <Loading />
        </div>
      )}

      {allOffers.length > 0 && (
        <div className="flex justify-end mt-8">
          <Button className="group" onClick={handleClickMarkAsRead} icon={MdOutlineMarkEmailRead}>
            Mark all as read
          </Button>
        </div>
      )}
    </div>
  )
}
